/**
 * GroupRowRenderer — Renders a group header row for DynamicTableV2 groupBy feature.
 *
 * Displays:
 * - Expand/collapse control
 * - Group value/label
 * - Row count
 * - Aggregate values for columns that have aggregate defined
 */

import React from 'react';
import { Button } from '@packages/ui/primitives/Button';
import type { InternalColumnDef, InternalActionDef } from '../schema/types';
import type { GroupedRow } from './useGroupedData';
import { ActionRenderer } from './ActionRenderer';
import { TABLE_IDS } from '../semantic-contract';
import { formatAggregateValue } from '../../utils/formatAggregateValue';

interface GroupRowRendererProps {
  /** The group row data */
  groupRow: GroupedRow;
  /** Column definitions */
  columns: ReadonlyArray<InternalColumnDef>;
  /** Whether to show checkbox column */
  showCheckbox: boolean;
  /** Whether to show actions column */
  showActionsColumn: boolean;
  /** Field being grouped by */
  groupByField: string;
  /** Registry namespace for action handlers */
  namespace: string;
  /** Callback to toggle group expansion */
  onToggleExpand: (groupKey: string) => void;
  /** Whether to hide the grouped column */
  hideGroupedColumn: boolean;
  /**
   * Row-level actions from the schema.
   * When provided, an ActionRenderer is rendered on the group header row
   * (matching V1 behaviour where the `:` menu lives on the group row, not
   * the individual child rows).
   * The first child row's data is passed as the action context so action
   * handlers can access createdDate / group-level fields.
   */
  rowActions?: ReadonlyArray<InternalActionDef>;
  /** Callback forwarded to ActionRenderer */
  onExecuteAction?: (action: InternalActionDef, row: Record<string, unknown>) => void;
  /** Permission checker forwarded to ActionRenderer */
  checkPermission?: (permission: { action: string; resource: string }) => boolean;
  /** When true, the expand/collapse button is hidden (alwaysExpanded mode) */
  hideExpandButton?: boolean;
  /** Extra invisible columns added to accommodate sub-rows with more data columns than the parent. */
  subRowOverflowCols?: number;
}

export const GroupRowRenderer: React.FC<GroupRowRendererProps> = ({
  groupRow,
  columns,
  showCheckbox,
  showActionsColumn,
  groupByField,
  namespace,
  onToggleExpand,
  hideGroupedColumn,
  rowActions,
  onExecuteAction,
  checkPermission,
  hideExpandButton = false,
  subRowOverflowCols = 0,
}) => {
  const { __groupKey, __groupLabel, __aggregates, __isExpanded, __subRows } = groupRow;

  // Use the first child row as the action context so action handlers
  // (e.g. "View Details" navigating with createdDate) receive group-level
  // field values — matching V1's behaviour where the group row carried those values.
  const representativeRow: Record<string, unknown> = __subRows[0] ?? {};
  const hasGroupActions =
    showActionsColumn && rowActions != null && rowActions.length > 0 && onExecuteAction != null;

  return (
    <tr
      className='group-row'
      data-testid={`dt-v2-group-row-${__groupKey}`}
      style={{
        backgroundColor: __isExpanded ? '#f3f0ff' : 'transparent',
        borderBottom: '1px solid #e5e7eb',
      }}>
      {/* Checkbox column placeholder */}
      {showCheckbox && (
        <td style={{ paddingLeft: 8, verticalAlign: 'middle', width: 40 }}>
          {/* Group rows don't have individual checkboxes */}
        </td>
      )}

      {/* Expand/collapse button in its own td — matches legacy V1 layout */}
      <td
        style={{
          paddingLeft: 10,
          paddingTop: 12,
          paddingBottom: 12,
          verticalAlign: 'middle',
          width: hideExpandButton ? 0 : 40,
        }}
        onClick={hideExpandButton ? undefined : () => onToggleExpand(__groupKey)}>
        {!hideExpandButton && (
          <Button
            semanticId={TABLE_IDS.expandGroup(__groupKey)}
            type='button'
            variant='small-icon-dark'
            size='sm'
            icon={__isExpanded ? 'keyboard_arrow_down' : 'chevron_right'}
            onClick={(e) => {
              e.stopPropagation();
              onToggleExpand(__groupKey);
            }}
            data-testid={`dt-v2-group-expand-${__groupKey}`}
          />
        )}
      </td>

      {/* Render data cells — first column spans one less since expand button has its own td */}
      {columns.map((col, colIndex) => {
        const isGroupedColumn = col.field === groupByField;
        const aggregateValue = __aggregates[col.field];
        const hasAggregate = col.aggregate != null && aggregateValue != null;

        if (colIndex === 0) {
          return (
            <td
              key={col.id}
              style={{
                textAlign: col.align,
                paddingTop: 12,
                paddingBottom: 12,
              }}
              data-testid={`dt-v2-group-cell-${__groupKey}-${col.field}`}>
              {isGroupedColumn
                ? __groupLabel
                : hasAggregate
                ? formatAggregateValue(aggregateValue, col.aggregate!)
                : ''}
            </td>
          );
        }

        if (isGroupedColumn && !hideGroupedColumn) {
          return (
            <td
              key={col.id}
              style={{
                textAlign: col.align,
                paddingTop: 12,
                paddingBottom: 12,
              }}
              data-testid={`dt-v2-group-cell-${__groupKey}-${col.field}`}>
              {__groupLabel}
            </td>
          );
        }

        return (
          <td
            key={col.id}
            style={{
              textAlign: col.align,
              paddingTop: 12,
              paddingBottom: 12,
            }}
            data-testid={`dt-v2-group-cell-${__groupKey}-${col.field}`}>
            {hasAggregate ? formatAggregateValue(aggregateValue, col.aggregate!) : ''}
          </td>
        );
      })}

      {subRowOverflowCols > 0 &&
        Array.from({ length: subRowOverflowCols }, (_, i) => (
          <td key={`overflow-${i}`} />
        ))}
      {/* Actions column — render the action menu on the group header row, matching V1 */}
      {showActionsColumn && (
        <td style={{ paddingTop: 12, paddingBottom: 12, overflow: 'hidden' }}>
          {hasGroupActions && (
            <ActionRenderer
              actions={rowActions!}
              row={representativeRow}
              rowIndex={0}
              namespace={namespace}
              onExecuteAction={onExecuteAction!}
              checkPermission={checkPermission}
            />
          )}
        </td>
      )}
    </tr>
  );
};

export default GroupRowRenderer;
